/* eslint-disable max-lines-per-function, max-statements, max-lines */
import { useLazyQuery, useMutation } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import { reviewsServiceNames } from "config/site/serviceNames";
import { DEFAULT_HOME_VIDEO_REVIEW } from "contexts/Reviews";
import useAddEditHomeVideoReview from "hooks/useAddEditHomeVideoReview";
import useCreateReviews from "hooks/useCreateReviews";
import useReviews from "hooks/useReviews";
import equals from "ramda/src/equals";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import EditActions from "./EditActions";
import getIsHomeVideoReviewReadyToBeUpdated from "./helpers/getIsHomeVideoReviewReadyToBeUpdated";
import getIsUpdateHomeVideoReviewEnabled from "./helpers/getIsUpdateHomeVideoReviewEnabled";
const { homeVideoReviews: { UPDATE_HOME_VIDEO_REVIEW } } = mutations;
const { homeVideoReviews: { GET_HOME_VIDEO_REVIEW, SEARCH_HOME_VIDEO_REVIEWS } } = queries;
// Define the default value for a selected HomeVideoReview
const DEFAULT_SELECTED_HOME_VIDEO_REVIEW = "";
// This function prepares the component with the necessary data
const usePrepareComponent = () => {
    // Using custom hooks for various actions and state management
    const { resetSelectedReviews, setIMDbId, setSelectedHomeVideoReview, state: { avForums, avNirvana, avsForum, bluRay, bluRayAuthority, digitalBits, doBlu, hidefDigest, highDefDiscNews, homeTheaterForum, isUHD, homeVideoReview, theaterByte, whySoBlu } } = useReviews();
    const { areReviewsCalled, areReviewsLoading, onCreateReviews, resetReviews } = useCreateReviews();
    const { onClearAll } = useAddEditHomeVideoReview();
    const [selectedHomeVideoReviewId, setSelectedHomeVideoReviewId] = useState(DEFAULT_SELECTED_HOME_VIDEO_REVIEW);
    // Using Apollo client hooks for data fetching and updating
    const [searchHomeVideoReviews, { data: searchHomeVideoReviewsData, loading: searchHomeVideoReviewsLoading }] = useLazyQuery(SEARCH_HOME_VIDEO_REVIEWS);
    const [updateHomeVideoReview, { called: updateHomeVideoReviewCalled, data: updateHomeVideoReviewData, loading: updateHomeVideoReviewLoading, reset: updateHomeVideoReviewReset }] = useMutation(UPDATE_HOME_VIDEO_REVIEW);
    const [getHomeVideoReview, { loading: getHomeVideoReviewLoading }] = useLazyQuery(GET_HOME_VIDEO_REVIEW);
    // Determine if the HomeVideoReview is ready to be updated
    const isHomeVideoReviewReadyToBeUpdated = useMemo(() => getIsHomeVideoReviewReadyToBeUpdated({
        avForums: avForums.selected,
        avNirvana: avNirvana.selected,
        avsForum: avsForum.selected,
        bluRay: bluRay.selected,
        bluRayAuthority: bluRayAuthority.selected,
        digitalBits: digitalBits.selected,
        doBlu: doBlu.selected,
        hidefDigest: hidefDigest.selected,
        highDefDiscNews: highDefDiscNews.selected,
        homeTheaterForum: homeTheaterForum.selected,
        homeVideoReview,
        id: selectedHomeVideoReviewId,
        theaterByte: theaterByte.selected,
        whySoBlu: whySoBlu.selected
    }), [
        homeVideoReview,
        avForums.selected,
        avNirvana.selected,
        avsForum.selected,
        bluRay.selected,
        bluRayAuthority.selected,
        digitalBits.selected,
        doBlu.selected,
        hidefDigest.selected,
        highDefDiscNews.selected,
        homeTheaterForum.selected,
        selectedHomeVideoReviewId,
        theaterByte.selected,
        whySoBlu.selected
    ]);
    // If the reviews are submitted, check if the HomeVideoReview is ready to be updated and update it if necessary
    useEffect(() => {
        if (areReviewsCalled) {
            if (isHomeVideoReviewReadyToBeUpdated && !updateHomeVideoReviewCalled) {
                const { isBluRay: __, isUHD: ___, name: ____, ...preparedHomeVideoReview } = homeVideoReview;
                updateHomeVideoReview({
                    variables: {
                        homeVideoReview: {
                            ...preparedHomeVideoReview,
                            id: selectedHomeVideoReviewId
                        }
                    }
                });
            }
        }
    }, [
        areReviewsCalled,
        updateHomeVideoReview,
        updateHomeVideoReviewCalled,
        isHomeVideoReviewReadyToBeUpdated,
        homeVideoReview,
        selectedHomeVideoReviewId
    ]);
    // If the HomeVideoReview is updated, reset the selected reviews and HomeVideoReview ID
    useEffect(() => {
        if (updateHomeVideoReviewData) {
            if (!equals(homeVideoReview, DEFAULT_HOME_VIDEO_REVIEW)) {
                resetSelectedReviews();
                updateHomeVideoReviewReset();
            }
            resetReviews();
            setSelectedHomeVideoReviewId("");
        }
    }, [
        homeVideoReview,
        resetReviews,
        resetSelectedReviews,
        setSelectedHomeVideoReviewId,
        updateHomeVideoReviewData,
        updateHomeVideoReviewReset
    ]);
    // Determine if the update HomeVideoReview button should be enabled
    const isUpdateHomeVideoReviewEnabled = useMemo(() => getIsUpdateHomeVideoReviewEnabled({
        avForums: avForums.selected,
        avNirvana: avNirvana.selected,
        avsForum: avsForum.selected,
        bluRay: bluRay.selected,
        bluRayAuthority: bluRayAuthority.selected,
        digitalBits: digitalBits.selected,
        doBlu: doBlu.selected,
        hidefDigest: hidefDigest.selected,
        highDefDiscNews: highDefDiscNews.selected,
        homeTheaterForum: homeTheaterForum.selected,
        homeVideoReview,
        id: selectedHomeVideoReviewId,
        theaterByte: theaterByte.selected,
        whySoBlu: whySoBlu.selected
    }), [
        avForums.selected,
        avNirvana.selected,
        avsForum.selected,
        bluRay.selected,
        bluRayAuthority.selected,
        digitalBits.selected,
        doBlu.selected,
        hidefDigest.selected,
        highDefDiscNews.selected,
        homeTheaterForum.selected,
        homeVideoReview,
        selectedHomeVideoReviewId,
        theaterByte.selected,
        whySoBlu.selected
    ]);
    // Check loading state of various API calls
    const loading = useMemo(() => updateHomeVideoReviewLoading ||
        areReviewsLoading ||
        getHomeVideoReviewLoading ||
        searchHomeVideoReviewsLoading, [
        areReviewsLoading,
        updateHomeVideoReviewLoading,
        getHomeVideoReviewLoading,
        searchHomeVideoReviewsLoading
    ]);
    const setSelectedHomeVideoReviewIdByServiceName = useCallback((data) => (serviceName) => {
        const service = data[serviceName];
        if (service && service.id !== homeVideoReview?.[`${serviceName}Id`]) {
            setSelectedHomeVideoReview({
                id: service.id,
                isCreated: true,
                isPreviewValid: true,
                isPublished: data.isPublished,
                serviceName,
                title: data.name,
                url: service.url ?? ""
            });
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        // All we need is each service name id
        homeVideoReview.avForumsId,
        homeVideoReview.avNirvanaId,
        homeVideoReview.avsForumId,
        homeVideoReview.bluRayId,
        homeVideoReview.bluRayAuthorityId,
        homeVideoReview.digitalBitsId,
        homeVideoReview.doBluId,
        homeVideoReview.hidefDigestId,
        homeVideoReview.highDefDiscNewsId,
        homeVideoReview.homeTheaterForumId,
        homeVideoReview.theaterByteId,
        homeVideoReview.whySoBluId,
        setSelectedHomeVideoReview
    ]);
    // When a HomeVideoReview is selected, fetch its data and update the form values
    const onSelectHomeVideoReviewId = useCallback((id) => {
        setSelectedHomeVideoReviewId(id ?? "");
        getHomeVideoReview({
            onCompleted: (response) => {
                const data = response.getHomeVideoReview;
                if (data) {
                    if (data.mas?.imdbId &&
                        data.mas.imdbId !== homeVideoReview.IMDbId) {
                        setIMDbId(data.mas.imdbId);
                    }
                    // Set the selected HomeVideoReview for each service
                    reviewsServiceNames.forEach(setSelectedHomeVideoReviewIdByServiceName(data));
                }
            },
            variables: {
                id
            }
        });
    }, [
        getHomeVideoReview,
        homeVideoReview.IMDbId,
        setIMDbId,
        setSelectedHomeVideoReviewIdByServiceName
    ]);
    // Clear all form values and deselect the HomeVideoReview ID
    const onClearAllAndSelected = useCallback(() => {
        setSelectedHomeVideoReviewId("");
        onClearAll();
    }, [onClearAll]);
    const onGetSuggestions = useCallback(({ inputValue }) => {
        searchHomeVideoReviews({
            variables: {
                searchHomeVideoReviewsInput: {
                    isUHD,
                    title: inputValue
                }
            }
        });
    }, [isUHD, searchHomeVideoReviews]);
    const homeVideoReviewTitles = useMemo(() => searchHomeVideoReviewsData?.searchHomeVideoReviews ?? [], [searchHomeVideoReviewsData]);
    // Return the necessary props for the component
    return {
        homeVideoReviewTitles,
        isUpdateHomeVideoReviewEnabled,
        loading,
        onClearAll: onClearAllAndSelected,
        onGetSuggestions,
        onSelectHomeVideoReviewId,
        onUpdateHomeVideoReview: onCreateReviews,
        selectedHomeVideoReviewId
    };
};
// Export the prepared component
export default prepareComponent(usePrepareComponent)(memo(EditActions));
